import { useState, useEffect } from "react";
import { fetchUserSubscriptionData } from "../../utils/SubscriptionUtil";
import SubscriptionsAPI from "../../services/SubscriptionsAPI";
import PaymentAPI from "../../services/PaymentAPI";
import { useUser } from "../../context/UserContext";

export const useSubscriptionData = () => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentServicePlan, setCurrentServicePlan] = useState(null);

  // Get user data from UserContext
  const { loggedInUser } = useUser();

  // Only the logged-in user can have subscriptions
  const currentUser = loggedInUser;

  const fetchServicePlanDetails = async (planId) => {
    try {
      console.log(
        "📡 Subscriptions: Fetching service plan details for planId:",
        planId
      );

      const response = await SubscriptionsAPI.getServicePlanById(planId);

      console.log("🌐 Subscriptions: Service plan API response:", response);

      if (response && response.success && response.data) {
        console.log(
          "✅ Subscriptions: Service plan details received:",
          response.data
        );
        setCurrentServicePlan(response.data);
      } else if (response && response.data) {
        // Handle case where response doesn't have success flag but has data
        console.log(
          "✅ Subscriptions: Service plan details received (no success flag):",
          response.data
        );
        setCurrentServicePlan(response.data);
      } else {
        console.error(
          "❌ Subscriptions: Service plan API returned unsuccessful response:",
          response
        );
        setCurrentServicePlan(null);
      }
    } catch (error) {
      console.error(
        "💥 Subscriptions: Error fetching service plan details:",
        error
      );
      setCurrentServicePlan(null);
    }
  };

  const fetchSubscriptionData = async () => {
    console.log(
      "🔄 Subscriptions: Starting fetchSubscriptionData with UserContext..."
    );

    try {
      setLoading(true);
      console.log("⏳ Subscriptions: Loading state set to true");

      // Check if we have a valid user from UserContext
      if (!currentUser || !currentUser.uuid) {
        console.error(
          "❌ Subscriptions: No authenticated user found in UserContext"
        );
        setError("User not authenticated");
        return;
      }

      console.log("👤 Subscriptions: Using logged-in user:", {
        uuid: currentUser.uuid,
        name: `${currentUser.firstName} ${currentUser.lastName}`,
      });

      // Use the centralized subscription utility with UserContext data
      const subscriptionData = await fetchUserSubscriptionData(currentUser);

      console.log(
        "✅ Subscriptions: Subscription data received:",
        subscriptionData
      );

      // Update component state with the fetched data
      setCurrentSubscription(subscriptionData.currentSubscription);
      setSubscriptionHistory(subscriptionData.subscriptionHistory);

      // Fetch service plan details if we have a current subscription with planId
      if (
        subscriptionData.currentSubscription &&
        subscriptionData.currentSubscription.planId
      ) {
        console.log(
          "🔄 Subscriptions: Fetching service plan details for planId:",
          subscriptionData.currentSubscription.planId
        );
        await fetchServicePlanDetails(
          subscriptionData.currentSubscription.planId
        );
      } else {
        console.log(
          "ℹ️ Subscriptions: No planId found, clearing service plan details"
        );
        setCurrentServicePlan(null);
      }

      // Clear any previous errors
      setError(null);
    } catch (error) {
      console.error(
        "💥 Subscriptions: Error fetching subscription data:",
        error
      );
      setError(error.message || "Failed to load subscription information");
    } finally {
      console.log(
        "✅ Subscriptions: fetchSubscriptionData completed, setting loading to false"
      );
      setLoading(false);
    }
  };

  const fetchPaymentData = async () => {
    console.log(
      "🔄 Subscriptions: Starting fetchPaymentData with UserContext..."
    );

    try {
      // Check if we have a valid user from UserContext
      if (!currentUser || !currentUser.uuid) {
        console.error(
          "❌ Subscriptions: No authenticated user found in UserContext for payment data"
        );
        return;
      }

      const uuid = currentUser.uuid;
      console.log("💳 Subscriptions: Fetching payment data for UUID:", uuid);

      // Fetch current payment details
      const currentPaymentResponse =
        await PaymentAPI.getCurrentPaymentByUserUuid(uuid);
      console.log(
        "💳 Subscriptions: Current payment API response:",
        currentPaymentResponse
      );

      if (currentPaymentResponse && currentPaymentResponse.success) {
        console.log(
          "✅ Subscriptions: Current payment data found:",
          currentPaymentResponse.data
        );
        setPaymentData(currentPaymentResponse.data);
      } else {
        console.log("ℹ️ Subscriptions: No current payment data found");
        setPaymentData(null);
      }

      // Fetch payment history
      const paymentHistoryResponse =
        await PaymentAPI.getPaymentHistoryByUserUuid(uuid);
      console.log(
        "💳 Subscriptions: Payment history API response:",
        paymentHistoryResponse
      );

      if (paymentHistoryResponse && paymentHistoryResponse.success) {
        console.log(
          "✅ Subscriptions: Payment history found:",
          paymentHistoryResponse.data
        );
        setPaymentHistory(paymentHistoryResponse.data || []);
      } else {
        console.log("ℹ️ Subscriptions: No payment history found");
        setPaymentHistory([]);
      }
    } catch (error) {
      console.error("💥 Subscriptions: Error fetching payment data:", error);
    }
  };

  // Effect to fetch data when logged-in user changes
  useEffect(() => {
    console.log(
      "🔄 Subscriptions: Logged-in user changed, checking if we should fetch data..."
    );

    if (currentUser && currentUser.uuid) {
      console.log(
        "👤 Subscriptions: Valid logged-in user detected, fetching subscription data"
      );
      fetchSubscriptionData();
      fetchPaymentData();
    } else {
      console.log("🚫 Subscriptions: No logged-in user, clearing data");
      setCurrentSubscription(null);
      setSubscriptionHistory([]);
      setPaymentData(null);
      setPaymentHistory([]);
      setCurrentServicePlan(null);
      setLoading(false);
      setError("User not authenticated");
    }
  }, [currentUser?.uuid]); // Only depend on loggedInUser UUID

  // Public function to manually refresh subscription data
  const refreshSubscriptionData = async () => {
    console.log("🔄 Subscriptions: Manual refresh requested");
    if (currentUser && currentUser.uuid) {
      await fetchSubscriptionData();
      await fetchPaymentData();
    } else {
      console.error("❌ Subscriptions: Cannot refresh - no authenticated user");
      setError("User not authenticated");
    }
  };

  return {
    currentSubscription,
    subscriptionHistory,
    paymentData,
    paymentHistory,
    currentServicePlan,
    loading,
    error,
    user: currentUser, // Expose logged-in user for convenience
    refreshSubscriptionData, // Allow manual refresh
    fetchSubscriptionData, // Keep for backward compatibility
    fetchPaymentData, // Keep for backward compatibility
  };
};
